import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";

const sections = [
    {
        n: 1,
        title: "Acceptance of Terms",
        body: (
            <>
                Welcome to <strong>Vive a lo Grande</strong> ("we", "us", "the Service"), accessible at Vivealogrande.com. By creating an account, signing in, or using the Service, you agree to these Terms of Service. If you disagree with any part, please do not use the Service.
            </>
        ),
    },
    {
        n: 2,
        title: "Description of Service",
        body: (
            <>
                Vive a lo Grande is a personal finance management application that lets you log income and expenses, create budgets, set savings goals, track net worth, import bank statements via CSV, and chat with an AI assistant about your finances. All the data you enter belongs to you.
            </>
        ),
    },
    {
        n: 3,
        title: "Account & Authentication",
        body: (
            <>
                You need a Google-authenticated account to use the Service. You are responsible for keeping your Google credentials confidential and for all activity under your session. Notify us immediately of any unauthorized use at <a className="underline decoration-[hsl(var(--accent))]" href="mailto:vivealograndeus@gmail.com">vivealograndeus@gmail.com</a>.
            </>
        ),
    },
    {
        n: 4,
        title: "Acceptable Use",
        body: (
            <>
                You agree not to: (a) use the Service for unlawful purposes; (b) attempt unauthorized access to other accounts or systems; (c) upload malicious content or code; (d) resell or sublicense the Service without written permission.
            </>
        ),
    },
    {
        n: 5,
        title: "Your Content & Data Ownership",
        body: (
            <>
                You retain all rights to the transactions, budgets, goals, and financial information you enter ("Your Content"). You grant us a limited, non-exclusive, revocable license to process Your Content solely to operate the Service. We do not sell Your Content to third parties.
            </>
        ),
    },
    {
        n: 6,
        title: "Money Coach",
        body: (
            <>
                The "Caesar" money coach uses third-party language models and provides information for educational purposes only. It does not constitute professional financial, accounting, tax, or investment advice. Consult a certified professional for material decisions. Automated responses may be inaccurate.
            </>
        ),
    },
    {
        n: 7,
        title: "Intellectual Property",
        body: (
            <>
                The Service, including its code, design, brand, logos, and original content, is the property of Vive a lo Grande and is protected under U.S. and international IP laws. Nothing here grants you the right to use our names or marks without prior written consent.
            </>
        ),
    },
    {
        n: 8,
        title: "Termination",
        body: (
            <>
                You may close your account at any time by contacting <a className="underline decoration-[hsl(var(--accent))]" href="mailto:vivealograndeus@gmail.com">vivealograndeus@gmail.com</a>. We reserve the right to suspend or terminate access for violation of these Terms, with reasonable notice when practical.
            </>
        ),
    },
    {
        n: 9,
        title: "Disclaimer of Warranties",
        body: (
            <>
                The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, or non-infringement.
            </>
        ),
    },
    {
        n: 10,
        title: "Limitation of Liability",
        body: (
            <>
                To the fullest extent permitted by law, Vive a lo Grande shall not be liable for indirect, incidental, special, consequential, or punitive damages, nor for lost profits, revenue, data, or goodwill arising from use of the Service.
            </>
        ),
    },
    {
        n: 11,
        title: "Changes",
        body: (
            <>
                We may update these Terms from time to time. We will post the updated version in the Service with a new effective date. Continued use constitutes acceptance.
            </>
        ),
    },
    {
        n: 12,
        title: "Governing Law",
        body: (
            <>
                These Terms are governed by the laws of the State of Tennessee, United States of America. Disputes shall be resolved exclusively in the state or federal courts located in Tennessee, unless applicable law provides otherwise.
            </>
        ),
    },
    {
        n: 13,
        title: "Contact",
        body: (
            <>
                Questions or requests? Reach us at <a className="underline decoration-[hsl(var(--accent))]" href="mailto:vivealograndeus@gmail.com">vivealograndeus@gmail.com</a>.
            </>
        ),
    },
];

const Terms = () => {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="terms-page">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-[hsl(var(--border))]">
                <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <button
                        data-testid="terms-back-button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
                        <BrandLogo size={26} />
                        <span className="font-display font-bold text-base tracking-tight text-[hsl(var(--foreground))]">Vive a lo Grande</span>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--accent))] uppercase">
                            Legal
                        </span>
                        <span className="h-px w-14 bg-[hsl(var(--muted-foreground))]/40" />
                    </div>
                    <h1 className="font-display font-extrabold text-[hsl(var(--foreground))] leading-[1.05] tracking-[-0.02em] text-4xl md:text-6xl">
                        Terms of Service
                    </h1>
                    <p className="mt-4 text-[hsl(var(--muted-foreground))]">
                        <span className="font-semibold text-[hsl(var(--foreground))]">Last updated:</span> February 1, 2026
                    </p>
                </motion.div>

                <div className="mt-10 md:mt-14 space-y-8">
                    {sections.map((s) => (
                        <section key={s.n} data-testid={`terms-section-${s.n}`} className="scroll-mt-24">
                            <div className="flex items-baseline gap-4">
                                <span className="font-display font-bold text-[hsl(var(--accent))] text-lg md:text-xl w-8 shrink-0">
                                    {String(s.n).padStart(2, "0")}
                                </span>
                                <h2 className="font-display font-bold text-[hsl(var(--foreground))] text-xl md:text-2xl tracking-tight">
                                    {s.title}
                                </h2>
                            </div>
                            <div className="mt-3 pl-12 text-[hsl(var(--foreground))]/85 leading-relaxed">
                                {s.body}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-16 pt-8 border-t border-[hsl(var(--border))] flex items-center justify-between gap-4 flex-wrap">
                    <a
                        href="mailto:vivealograndeus@gmail.com"
                        className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                        data-testid="terms-contact-link"
                    >
                        <Mail className="w-4 h-4" /> vivealograndeus@gmail.com
                    </a>
                    <Button
                        data-testid="terms-return-home"
                        onClick={() => navigate("/")}
                        className="pill bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"
                    >
                        Return home
                    </Button>
                </div>
            </main>
        </div>
    );
};

export default Terms;
